/**
 * The `Arithmetic` catalog component: worked steps to a figure.
 *
 * 9 instances in the design as a card (13i, 13w, 19a), and 31 more where the
 * same working appears inline in prose — which is the argument for drawing
 * it. A reader told "you receive Rs 950" has to take it; a reader shown the
 * three lines that produced it can check one.
 *
 * ROW ORDER IS THE PROOF. The server sends the steps in the order they were
 * worked and nothing here sorts them: "You receive Rs 950" is only true
 * after the two lines above it, and re-ordering would turn a proof into
 * three unrelated figures.
 *
 * The LAST line is the answer and gets an inset result panel. It is a weight
 * difference and not a colour one: an entitlement is neither good news nor
 * bad, and green here would be this surface congratulating someone on their
 * own dividend.
 *
 * The expression is the server's own string — "65 × 12%" — and is never
 * parsed here. It says how the figure beside it was reached; the figure is
 * the `_measure` and the only thing that goes through `Fig`.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { CellValue } from '@/features/mitra/lib/figures'
import { captionLabel } from '@/features/mitra/lib/figures'
import { Fig, Label, SCROLL_X } from '@/features/mitra/panel/blocks/Card'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const ArithmeticApi = {
  name: 'Arithmetic',
  schema: z
    .object({
      steps: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/steps`, fixed by `to_messages`. */
export type StepPart = {
  id: string
  label: string
  expression: string
  value: CellValue
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const arithmeticApi = ArithmeticApi as unknown as ComponentApi

export const Arithmetic = createComponentImplementation<ComponentApi>(
  arithmeticApi,
  ({ props }: { props: { steps?: unknown; caption?: unknown } }) => (
    <StepList
      steps={listOf<StepPart>(props.steps)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/** The drawing, given the list the surface resolved. */
function StepList({ steps, caption }: { steps: StepPart[]; caption: string | null }) {
  if (steps.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }
  const last = steps.length - 1
  return (
    <figure
      data-role="chart"
      data-component="arithmetic"
      className="m-0 flex flex-col gap-2 px-3 pb-3 pt-3"
    >
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}
      <ol className="m-0 flex list-none flex-col p-0">
        {steps.map((s, i) => (
          <li
            key={s.id}
            data-role="step"
            data-final={i === last ? 'true' : 'false'}
            className={`min-w-0 ${
              i === last ? 'mt-2 rounded-block bg-ground px-3 py-3' : 'border-b border-rule py-2'
            }`}
          >
            <span className="block min-w-0">
              <span
                className={`block text-body-s leading-snug ${
                  i === last ? 'font-semibold text-ink' : 'text-ink'
                }`}
              >
                {s.label}
              </span>
              {s.expression && (
                <span
                  data-role="step-expression"
                  className="mt-1 block break-words font-mono text-label-s tabular-nums text-ink-soft"
                >
                  {s.expression}
                </span>
              )}
            </span>
            <span className={`mt-2 block max-w-full ${SCROLL_X}`}>
              <Fig
                cell={s.value}
                quietUnit={i === last}
                className={`tabular-nums ${
                  i === last ? 'text-mono-l font-semibold text-ink' : 'text-body-m text-ink'
                }`}
              />
            </span>
          </li>
        ))}
      </ol>
    </figure>
  )
}
